import getConfig from 'next/config';
import * as React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import BooksLabeledContainer from '../../../../components/books/model1/BooksLabeledContainer';
import BooksNotes from '../../../../components/books/model1/BooksNotes';
import BooksSection from '../../../../components/books/model1/BooksSection';
import BooksCompleteAlert from '../../../../components/books/model1/forms/BooksCompleteAlert';
import BooksRequestAlert from '../../../../components/books/model1/forms/BooksRequestAlert';
import BooksCenteringUnderLineMessage from '../../../../components/books/model1/texts/BooksCenteringUnderLineMessage';
import BooksSchoolItemList from '../../../../components/books/school/BooksSchoolItemList';
import {
  UserContextConsumer,
  UserContextState,
} from '../../../../contexts/UserContext';
import { CreateUserWithResvQuery } from '../../../../infrastructure/repositories/AuthRepository';
import * as IBooks from '../../../../interfaces/merchants/resources/books';
import * as IInputs from '../../../../interfaces/merchants/resources/books/inputs';
import {
  setMeToBookInfo,
  setPostable,
} from '../../../../redux/actions/merchants/resources/books';
import { Dispatch } from '../../../../redux/reducers/merchants/resources/books/inputs';
import * as gtag from '../../../../utils/gtag';
import Button, { ButtonSize } from '../../../common/buttons/Button';
import * as $ from './helper';
import InputCompletionCreateUser from './InputCompletionCreateUser';

const config = getConfig();
const publicRuntimeConfig = config ? config.publicRuntimeConfig : {};
const isProduction = publicRuntimeConfig.coubicEnv === 'production';

interface ElementProps {
  merchantResource: IBooks.MerchantResource;
  settings: IBooks.SettingCompletion;
  postable: boolean;
  publicId: string;
  withExpressFlow: boolean;
}

interface StateProps {
  inputs: IInputs.State;
}

interface DispatchProps {
  actions: {
    handleSetMe: (merchantId: string, resourceId: string) => Promise<void>;
    handlePostable: (postable: boolean) => void;
  };
}

type Props = ElementProps & StateProps & DispatchProps & InjectedIntlProps;

class InputCompletionSchool extends React.Component<
  Props,
  { submitted: boolean }
> {
  constructor(props: Props) {
    super(props);
    this.handleCreateUser = this.handleCreateUser.bind(this);
    this.state = {
      submitted: false,
    };
  }

  private async handleCreateUser(
    query: CreateUserWithResvQuery,
    userCtx: UserContextState,
  ) {
    const { inputs, actions } = this.props;
    actions.handlePostable(false);
    userCtx.auth
      .createUserWithResv(query)
      .then(async _ => {
        await actions.handleSetMe(inputs.merchantId!, inputs.resourceId!);
        this.setState({ submitted: true });
        actions.handlePostable(true);
      })
      .catch((_err: Error) => {
        actions.handlePostable(true);
      });
  }

  public async componentDidMount() {
    const { merchantResource, settings } = this.props;
    const reservationId = settings.createUserWithResv!.reservationId;

    if (isProduction) {
      // Fire GTM booking_completion event
      gtag.bookingEvent({
        step: 'completion',
        reservationPublicId: reservationId,
        resourcePublicId: merchantResource.publicId,
      });
    }
  }

  public render() {
    const {
      intl,
      merchantResource,
      settings,
      postable,
      withExpressFlow,
    } = this.props;
    const reservations = this.props.inputs;
    const schoolTimeSlot = reservations.schoolTimeSlots![0];

    return (
      <div>
        {this.props.settings.finishedStatus === 'accepted' && (
          <BooksCompleteAlert />
        )}
        {this.props.settings.finishedStatus === 'pending' && (
          <BooksRequestAlert />
        )}
        <BooksSection>
          <UserContextConsumer>
            {context =>
              settings.createUserWithResv!.available &&
              (this.state.submitted ? (
                <BooksCenteringUnderLineMessage>
                  {intl.formatMessage({
                    id: 'books.formsLabel.createUserSubmitted',
                  })}
                </BooksCenteringUnderLineMessage>
              ) : (
                <InputCompletionCreateUser
                  postable={postable}
                  reservationId={settings.createUserWithResv!.reservationId}
                  errors={context.auth.errors}
                  email={reservations.userInfo!.customerFields!.email}
                  onSubmit={(query: CreateUserWithResvQuery) =>
                    this.handleCreateUser(query, context)
                  }
                />
              ))
            }
          </UserContextConsumer>
        </BooksSection>
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.booksItems',
          })}
          paddingTop={true}
        >
          {schoolTimeSlot && (
            <BooksSchoolItemList
              nickname={this.props.publicId}
              resourceId={merchantResource.publicId}
              resourceName={merchantResource.name}
              resourceImage={
                merchantResource.primaryImage
                  ? merchantResource.primaryImage.thumb &&
                    merchantResource.primaryImage.thumb.url
                  : ''
              }
              resourceUrl={`/${this.props.publicId}/${merchantResource.publicId}`}
              startTime={schoolTimeSlot.startTime}
              endTime={schoolTimeSlot.endTime}
              instructors={schoolTimeSlot.instructors.map(i => i.name)}
              date={schoolTimeSlot.date}
              lessonName={reservations.lesson ? reservations.lesson.name : ''}
            />
          )}
        </BooksLabeledContainer>
        {!withExpressFlow && (
          <BooksSection withPadding={true} rightSide={true}>
            <Button
              isGhost={true}
              isFull={true}
              buttonSize={ButtonSize.Medium}
              href={`/${this.props.publicId}`}
            >
              {intl.formatMessage({
                id: 'books.formsLabel.backToMainPage',
              })}
            </Button>
          </BooksSection>
        )}
        <BooksLabeledContainer
          label={intl.formatMessage({
            id: 'books.formsLabel.attentionForBooking',
          })}
          contentsPadding={true}
          paddingTop={true}
        >
          <BooksNotes notes={$.createNotes(intl, settings)} />
        </BooksLabeledContainer>
      </div>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooksInputs,
}: {
  merchantsResourcesBooksInputs: IInputs.State;
}): StateProps => ({
  inputs: merchantsResourcesBooksInputs,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    handleSetMe: (merchantId: string, resourceId: string) =>
      setMeToBookInfo(merchantId, resourceId)(dispatch),
    handlePostable: (postable: boolean) => setPostable(postable)(dispatch),
  },
});

export default connect(
  mapStateProps,
  mapDispatchToProps,
)(injectIntl(InputCompletionSchool));
